import { VideoCSSVars } from "@videojs/media/dom";
//#region src/source.ts
/**
* What a Wistia player is started with here, whatever Wistia's default or the Wistia app says; a source overrides it.
* Only the corner radius so far, squared because the skin is what rounds a media — see `wistiaPlayerStyle`.
* `roundedPlayer` is the one to set: the three `*BorderRadius` options derive from it.
*/
const wistiaPlayerDefaultOptions = { roundedPlayer: 0 };
/**
* The style a Wistia player is given: the skin's corners, and no pointer events without chrome.
*
* `borderRadius` reads the same custom property a `<video>` does, and `overflow` goes with it — a `<video>` is replaced
* content that the radius alone clips, where Wistia paints into children the box has to clip too. `pointerEvents` is
* off for a chromeless player because the skin over it is what the viewer is clicking; left interactive it swallows
* those clicks and answers them with chrome of its own. The iframe embeds do the same through a
* `:host(:not([controls]))` rule in a template they own, which a player that brings its own element has no equivalent
* of.
*/
function wistiaPlayerStyle(controls) {
	return {
		borderRadius: `var(${VideoCSSVars.borderRadius})`,
		overflow: "hidden",
		pointerEvents: controls ? "auto" : "none"
	};
}
/**
* The eight control-bar switches `controls` drives as a group, since Wistia has no single chromeless flag.
* `playBarControl` doubles as the one the group is read back from.
*/
function wistiaControlProps(controls) {
	return {
		bigPlayButton: controls,
		controlsVisibleOnLoad: controls,
		fullscreenControl: controls,
		playBarControl: controls,
		playPauseControl: controls,
		playPauseNotifier: controls,
		settingsControl: controls,
		volumeControl: controls
	};
}
/**
* Extract a Wistia hashed id from a raw ten-character id or any recognized URL: media pages
* (`<account>.wistia.com/medias/<id>`), embed URLs (`fast.wistia.net/embed/iframe/<id>` and the `medias/<id>.jsonp` and
* `playlists/<id>` paths), the `wi.st` short host, and the `wvideo=<id>` parameter Wistia links carry.
*/
function parseWistiaMediaId(src) {
	if (!src) return null;
	if (MATCH_HASHED_ID.test(src)) return src;
	return MATCH_SRC.exec(src)?.[1] ?? MATCH_WVIDEO.exec(src)?.[1] ?? null;
}
/**
* Parse the `wtime` parameter of a Wistia URL into seconds. Wistia spells timestamps the way it spells them elsewhere:
* `90`, `90s`, `1m30s`, `1h2m3s`.
*/
function parseWistiaStartTime(src) {
	const value = /[?&]wtime=([\dhms]+)/i.exec(src)?.[1]?.toLowerCase();
	if (!value) return null;
	let seconds = null;
	for (const [pattern, multiplier] of WTIME_UNITS) {
		const amount = pattern.exec(value)?.[1];
		if (amount) seconds = (seconds ?? 0) + Number.parseInt(amount, 10) * multiplier;
	}
	return seconds;
}
/** The trailing `s` is optional, so the bare-number form (`90`) lands on the same pattern. */
const WTIME_UNITS = [
	[/(\d+)h/, 3600],
	[/(\d+)m/, 60],
	[/(\d+)s?$/, 1]
];
const MATCH_HASHED_ID = /^[a-z\d]{10}$/i;
const MATCH_SRC = /(?:wistia\.(?:com|net)|wi\.st)\/(?:medias|embed)\/(?:iframe\/|medias\/|playlists\/)?([a-z\d]{10})/i;
const MATCH_WVIDEO = /[?&]wvideo=([a-z\d]{10})/i;
//#endregion
export { parseWistiaMediaId, parseWistiaStartTime, wistiaControlProps, wistiaPlayerDefaultOptions, wistiaPlayerStyle };
